import { useEffect, useRef, useState } from 'react';

import { Box, InputLabel, Stack, Typography, useTheme } from '@mui/material';

import { AddressApi } from '@/api';
import { IcPlusCircle } from '@/assets/images';
import { IAddressForm } from '@/models';
import { useToastStore } from '@/store';

import { AddressOptionType, AddressSearch } from './AddressSearch';
import { BaseFieldProps } from '../../FormElements';

interface AddressItemProps extends BaseFieldProps {
  name?: string;
  value?: AddressOptionType | null;
  updateCurrentAddress: (newAddress: IAddressForm | null) => void;
  disabled?: boolean;
  showTruncatedTooltip?: boolean;
  enableBusinessName?: boolean;
}

export const SubAddressItem = (props: AddressItemProps) => {
  const CREATE_SUB_ADDRESS_PLACE_ID = 'create-sub-address';
  const {
    value,
    updateCurrentAddress,
    disabled,
    showTruncatedTooltip,
    enableBusinessName,
  } = props;
  const subAddressInputRef = useRef<HTMLInputElement>();
  const { updateToast } = useToastStore();
  const theme = useTheme();
  const [inputValue, setInputValue] = useState<string>('');
  const [subAddressOptions, setSubAddressOptions] = useState<IAddressForm[]>(
    [],
  );

  const { addressNumber, completeStreetName, zipcode, businessName } =
    value || {};

  const enableSubAddressField =
    !!addressNumber || !!completeStreetName || !!zipcode;
  const subAddressValue = enableSubAddressField ? value : null;
  const subAddressSearchKey = enableSubAddressField
    ? `${addressNumber || ''}:${completeStreetName || ''}:${zipcode || ''}`
    : 'sub-address-disabled';

  useEffect(() => {
    if (enableSubAddressField) {
      fetchAddress();
    } else {
      setSubAddressOptions([]);
    }
  }, [addressNumber, completeStreetName, zipcode, enableSubAddressField]);

  const fetchAddress = async () => {
    try {
      const params = {
        filter: JSON.stringify({
          addressNumber,
          completeStreetName,
          zipcode,
        }),
      };
      const res = await AddressApi.getAddresses(params);
      setSubAddressOptions(res.data.results);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const addNewAddress = async (newSubAddressValue = inputValue) => {
    try {
      if (value) {
        const newAddressData: IAddressForm = {
          ...value,
          _id: undefined,
          stateAddressPointId: undefined,
          // In CFS mode the value may carry a transient Google business name
          // that must not persist onto a new master sub-address record.
          ...(enableBusinessName ? { businessName: undefined } : {}),
          subAddress: newSubAddressValue,
          pointType: 5,
          point: {
            type: 'Point',
            coordinates: value.point?.coordinates || [0, 0],
          },
        };

        const res = await AddressApi.createAddress(newAddressData);
        setSubAddressOptions((v) => [...v, res.data]);
        updateCurrentAddress(res.data);
      }
    } catch (err: any) {
      updateToast({
        open: true,
        message: err.message || 'Failed to create sub-address.',
      });
    }
  };

  const handleChange = (newValue: AddressOptionType | string | null) => {
    if (!newValue) {
      const mainAddress = subAddressOptions.find(
        ({ subAddress }) => !subAddress,
      );
      if (mainAddress) {
        updateCurrentAddress({
          ...mainAddress,
          subAddress: '',
        });
      }
    } else if (
      typeof newValue !== 'string' &&
      newValue.placeId === CREATE_SUB_ADDRESS_PLACE_ID
    ) {
      addNewAddress(newValue.subAddress || inputValue);
    } else if (typeof newValue !== 'string') {
      updateCurrentAddress(newValue);
    }
  };

  const getSubAddressOptionLabel = (option?: AddressOptionType | string) => {
    if (typeof option === 'string') return option;
    return option?.subAddress || '';
  };

  return (
    <Stack className="address-item" alignItems="center">
      <Stack flex={1}>
        <Box
          sx={{
            position: 'relative',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          <InputLabel sx={{ mt: 1 }} disabled={!enableSubAddressField}>
            Sub-address
          </InputLabel>
          <AddressSearch
            key={subAddressSearchKey}
            inputRef={subAddressInputRef}
            value={subAddressValue}
            options={subAddressOptions.slice(1)}
            onChange={(_, selected) => {
              handleChange(selected);
            }}
            sx={{
              border: disabled ? '0px solid transparent' : '',
              borderRadius: '4px',
            }}
            getOptionLabel={getSubAddressOptionLabel}
            handleInputChange={setInputValue}
            showTruncatedTooltip={showTruncatedTooltip}
            isOptionEqualToValue={(option, v) => {
              if (typeof option === 'string' || typeof v === 'string')
                return false;
              if (
                option.placeId === CREATE_SUB_ADDRESS_PLACE_ID ||
                v.placeId === CREATE_SUB_ADDRESS_PLACE_ID
              ) {
                return false;
              }
              return option?.subAddress === v?.subAddress;
            }}
            renderOption={(option) => {
              if (!option?.subAddress) return null;
              if (option.placeId === CREATE_SUB_ADDRESS_PLACE_ID) {
                return (
                  <Typography
                    component="div"
                    color="error"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      minWidth: 0,
                      fontSize: '0.8125rem',
                      lineHeight: 1.2,
                      py: 0.5,
                    }}
                  >
                    <IcPlusCircle
                      style={{
                        marginRight: 4,
                        flexShrink: 0,
                        width: 18,
                        height: 18,
                      }}
                    />
                    <Box
                      component="span"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      Create{' '}
                      <Box
                        component="span"
                        sx={{ fontWeight: 700 }}
                      >{`"${option.subAddress}"`}</Box>
                    </Box>
                  </Typography>
                );
              }

              return (
                <Typography
                  color={theme.palette.text.primary}
                  variant="subtitle2"
                >
                  {option.subAddress}
                </Typography>
              );
            }}
            filterOptions={(options, state) => {
              const normalizedInputValue = state.inputValue
                .toLowerCase()
                .trim();
              const canCreate =
                Boolean(normalizedInputValue) &&
                !options.some(
                  (opt) =>
                    typeof opt !== 'string' &&
                    opt.subAddress?.toLowerCase().trim() ===
                      normalizedInputValue,
                );

              const filteredOptions = options.filter(
                (opt): opt is AddressOptionType => {
                  if (typeof opt === 'string' || !opt?.subAddress) {
                    return false;
                  }

                  if (!normalizedInputValue) {
                    return true;
                  }

                  return opt.subAddress
                    .toLowerCase()
                    .includes(normalizedInputValue);
                },
              );

              if (canCreate) {
                filteredOptions.push({
                  placeId: CREATE_SUB_ADDRESS_PLACE_ID,
                  subAddress: state.inputValue,
                } as AddressOptionType);
              }

              return filteredOptions;
            }}
            disabled={!enableSubAddressField || disabled}
          />
        </Box>
      </Stack>
      <Typography className="txt-busines-name">
        {enableBusinessName ? null : businessName}
      </Typography>
    </Stack>
  );
};
